// HealthPill — small status chip showing on-track / at-risk / off-track.
//
// Two modes:
//   - Display-only (cards, drawer): plain pill with hover tooltip showing
//     the auto reasons.
//   - Editable (project detail): the pill is a button that opens a menu to
//     pin a manual override or return to auto.
//
// The hover tooltip is CSS-positioned next to the pill; we don't try to
// portal it because it's small enough that the bounds rarely cause clipping.

import { useRef, useState } from 'react';

import { PROJECT_HEALTH_LABELS, PROJECT_HEALTH_ORDER } from '../../pm/constants';
import { HealthInfo } from '../../pm/health';
import { ProjectHealth } from '../../pm/types';
import PortalMenu from './PortalMenu';

interface Props {
  health: HealthInfo;
  /** When provided, the pill becomes an editor — click opens an override
   *  menu and we can pin / clear the manual state. */
  onSetManual?: (next: ProjectHealth | null) => void;
}

const HealthPill: React.FC<Props> = ({ health, onSetManual }) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const editable = !!onSetManual;
  const label = PROJECT_HEALTH_LABELS[health.state];
  const cls = `pm-health pm-health-${health.state}` + (health.manual ? ' is-pinned' : '');

  // Tooltip only renders when there's something to say. Pinned states get
  // an extra line so users know why the auto / displayed states differ.
  const hasReasons = health.reasons.length > 0;
  const showTip = hovered && !menuOpen && (hasReasons || health.manual);

  function pick(next: ProjectHealth | null) {
    onSetManual?.(next);
    setMenuOpen(false);
  }

  return (
    <span
      className="pm-health-wrap"
      ref={triggerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className={cls}
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : -1}
        onClick={(e) => {
          if (!editable) return;
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (!editable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setMenuOpen((v) => !v);
          }
        }}
        title={!editable && hasReasons ? undefined : 'Health'}
      >
        <span className={`pm-health-dot pm-health-dot-${health.state}`} />
        {label}
        {health.manual ? <span className="pm-health-pin" aria-label="Pinned by owner">·</span> : null}
      </span>

      {showTip ? (
        <span className="pm-health-tip" role="tooltip">
          <span className="pm-health-tip-hd">
            {health.manual
              ? `Pinned · auto says ${PROJECT_HEALTH_LABELS[health.auto].toLowerCase()}`
              : `Why ${label.toLowerCase()}?`}
          </span>
          {hasReasons ? (
            <ul>
              {health.reasons.map((r, i) => (
                <li key={i}>{r.text}</li>
              ))}
            </ul>
          ) : (
            <span className="pm-health-tip-empty">No active risk signals.</span>
          )}
        </span>
      ) : null}

      {editable ? (
        <PortalMenu
          open={menuOpen}
          triggerRef={triggerRef}
          onDismiss={() => setMenuOpen(false)}
          className="pm-health-menu"
          estimatedHeight={(PROJECT_HEALTH_ORDER.length + 1) * 36 + 16}
        >
          <div className="pm-health-menu-hd">
            Auto: <strong>{PROJECT_HEALTH_LABELS[health.auto]}</strong>
          </div>
          {PROJECT_HEALTH_ORDER.map((h) => (
            <button
              key={h}
              className={h === health.manual ? 'active' : ''}
              onClick={() => pick(h)}
            >
              <span className={`pm-health-dot pm-health-dot-${h}`} />
              Pin to {PROJECT_HEALTH_LABELS[h].toLowerCase()}
            </button>
          ))}
          {health.manual ? (
            <button onClick={() => pick(null)}>Return to auto</button>
          ) : null}
        </PortalMenu>
      ) : null}
    </span>
  );
};

export default HealthPill;
